import { getDeviceNumber } from '../../../scripts/common';
import { postEvent } from '../../../scripts/event';
import { deleteSensor } from '../../../scripts/sensor';
import { SensorIcon } from '../../UiComponents/Icons';
import CheckboxField from '../fields/CheckBoxField';
import NumberField from '../fields/NumberField';
import StatusField from '../fields/StatusField';
import { DisplayField } from '../fields/Styles';
import { hideFormNetworkField } from '../Hub/actions/FormNetwork';
import OpenNetworkButton from '../Hub/actions/OpenNetworkButton';
import { SensorZigbeeSetupButton } from '../Hub/actions/ZigbeeConfig';
import { hideInList } from '../tableUtils';
import { getSensorDisplayStatus, getSensorStatus, getSensorStatusColor } from '../utils/connectionStatus';
import { actionTypes } from '../utils/installationTypes';
import BatteryDisplayField from './fields/BatteryDisplayField';
import BlinkButtonField from './fields/BlinkButtonField';
import NodeTypeField from './fields/NodeTypeField';
import ZigbeePropsDisplayField from './fields/ZigbeePropsDisplayField';
import ZoneChoiceField, { getZoneFieldRequiredState } from './fields/ZoneChoiceField';
import { saveUpdateSensors } from './sensorUtils';

const idField = 'sensorId';

export const getSensorTableProps = ({ sensors, zones, hubs, customerId, serverStatus, displayMode }) => {

    const getColumns = (sensors) => [{
        key: 'sensorId',
        displayName: 'Id',
        width: '100px',
        readOnly: true,
        render: (sensor) => (
            <DisplayField>{sensor.sensorId}</DisplayField>
        )
    }, {
        displayName: 'Status',
        key: 'status',
        hide: hideInList(displayMode),
        render: (sensor) => {
            const listItemProps = getListItemProps(sensor)
            return <StatusField 
                displayStatus={listItemProps.displayStatus}
                statusColor={listItemProps.statusColor}
                error={listItemProps.error}
            />
        }
    }, {
        key: 'hubId',
        displayName: 'Hub',
        width: '100px',
        readOnly: true,
        render: (sensor) => (
            <DisplayField>{sensor.hubId}</DisplayField>
        )
    }, {
        key: 'zoneId',
        displayName: 'Zone',
        width: '120px',
        getRequiredState: (sensor) => getZoneFieldRequiredState(sensor, zones),
        render: (sensor, updateSensor, cellControl) => (
            <ZoneChoiceField
                sensor={sensor}
                updateSensor={updateSensor}
                zones={zones}
                cellControl={cellControl}
            />
        )
    }, {
        key: 'isConnected',
        displayName: 'Connected',
        width: '50px',
        readOnly: true,
        render: (sensor) => (
            <DisplayField>{sensor.isConnected}</DisplayField>
        )
    }, {
        key: 'version',
        displayName: 'Version',
        width: '50px',
        readOnly: true,
        render: (sensor) => (
            <DisplayField>{sensor.version}</DisplayField>
        )
    }, {
        key: 'nodeType',
        displayName: 'NodeType',
        width: '50px',
        readOnly: true,
        hide: hideInList(displayMode),
        render: (sensor) => (
            <NodeTypeField nodeType={sensor.nodeType} />
        )
    }, {
        key: 'enableOta',
        displayName: 'Enable Ota',
        width: '50px',
        readOnly: true,
        render: (sensor, updateSensor, cellControl) => (
            <CheckboxField
                checked={sensor.enableOta}
                setValue={() => updateSensor({ enableOta: !sensor.enableOta })}
                cellControl={cellControl}
            />
        )
    }, {
        key: 'otaDownloadPercentage',
        displayName: 'Ota download %',
        width: '50px',
        readOnly: true,
        render: (sensor) => (
            <DisplayField>{sensor.otaDownloadPercentage}</DisplayField>
        )
    }, {
        key: 'sleepLimit',
        displayName: 'SleepLimit (dB)',
        width: '50px',
        readOnly: true,
        render: (sensor, updateSensor, cellControl) => (
            <NumberField
                value={sensor.sleepLimit}
                setValue={(value) => updateSensor({ sleepLimit: value })}
                cellState={cellControl.cellState}
            />
        )
    }, {
        key: 'appMode',
        displayName: 'AppMode',
        width: '50px',
        readOnly: true,
        render: (sensor) => (
            <DisplayField>{sensor.appMode}</DisplayField>
        )
    }, {
        key: 'nodeId',
        displayName: 'NodeId',
        width: '50px',
        readOnly: true,
        hide: hideInList(displayMode),
        render: (sensor) => (
            <DisplayField>{sensor.nodeId}</DisplayField>
        )
    }, {
        key: 'parentNodeId',
        displayName: 'ParentNode',
        width: '50px',
        readOnly: true,
        hide: hideInList(displayMode),
        render: (sensor) => (
            <DisplayField>{sensor.parentNodeId}</DisplayField>
        )
    }, {
        key: 'lqi',
        displayName: 'LQI',
        width: '50px',
        readOnly: true,
        hide: hideInList(displayMode),
        render: (sensor) => (
            <DisplayField>{sensor.lqi}</DisplayField>
        )
    }, {
        key: 'batteryLevel',
        displayName: 'Battery%',
        width: '50px',
        readOnly: true,
        hide: hideInList(displayMode),
        render: (sensor) => (
            <DisplayField>{sensor.batteryLevel}%</DisplayField>
        )
    }, {
        key: 'batteryTemperature',
        displayName: 'Bat Temp',
        width: '50px',
        readOnly: true,
        hide: hideInList(displayMode),
        render: (sensor) => (
            <DisplayField>{sensor.batteryTemperature}°C</DisplayField>
        )
    }, {
        key: 'externalVoltage',
        displayName: 'Ext volt',
        width: '50px',
        readOnly: true,
        hide: hideInList(displayMode),
        render: (sensor) => (
            <DisplayField>{sensor.externalVoltage}V</DisplayField>
        )
    }, {
        key: 'blink',
        displayName: 'Blink',
        width: '50px',
        readOnly: true,
        hide: hideInList(displayMode),
        render: (sensor) => (
            <BlinkButtonField sensor={sensor} />
        )
    }]

    const saveObjects = async (changeSensors) => {
        try {
            await saveUpdateSensors(changeSensors, sensors, customerId);
        } catch (err) {
            console.log('Failed to update sensors');
        }
    }

    const deleteObject = async (sensor) => {
        try {
            await deleteSensor(customerId, sensor.hubId, sensor.sensorId);
            await postEvent(sensor.hubId, customerId, 'DELETE_SENSOR', { sensorId: sensor.sensorId });
        } catch (err) {
            console.log('Error failed to delete sensor');
        }
    }

    const getActions = (sensor) => [{
        key: 'blink',
        type: actionTypes.PRIMARY,
        render: () => (
            <BlinkButtonField sensor={sensor} />
        )
    }, {
        key: 'openNetwork',
        type: actionTypes.LIST,
        hide: !hideFormNetworkField(hubs),
        render: () => (
            <OpenNetworkButton hubs={hubs} />
        )
    }]

    const getListItemProps = (sensor) => {
        const hub = hubs.find(hub => hub.hubId === sensor.hubId);
        const status = getSensorStatus(sensor, hub, serverStatus);
        return {
            displayName: `Sensor ${getDeviceNumber(sensor)}`,
            Icon: SensorIcon,
            displayStatus: getSensorDisplayStatus(status),
            statusColor: getSensorStatusColor(status),
            renderCustomComponent: () => <>
                <BatteryDisplayField sensor={sensor} />
                <ZigbeePropsDisplayField sensor={sensor} />
            </>
        }
    }

    const sortObjects = (sensors) => {
        const groups = [];
        const zoneIds = zones.map(zone => zone.zoneId);
        if (sensors && sensors.length) {
            zones.forEach(zone => {
                const zoneSensors = sensors.filter(sensor => sensor.zoneId === zone.zoneId);
                if (zoneSensors.length) {
                    groups.push({
                        title: zone.zoneName,
                        objects: zoneSensors
                    })
                }
            });

            const sensorsWithoutZone = sensors.filter(sensor => !zoneIds.includes(sensor.zoneId));
            if (sensorsWithoutZone.length) {
                groups.push({
                    title: 'Without zone',
                    objects: sensorsWithoutZone
                })
            }
        }

        return groups;
    }

    const getPlaceholder = () => {
        return <div>
            {hideFormNetworkField(hubs) ? <>
                <p>No sensors have been added. Open the zigbee network and add sensors by double clicking the status-button</p>
                <OpenNetworkButton hubs={hubs} />
            </> : <>
                <p>A zigbee network has not yet been created. Press the button below to start zigbee setup.</p>
                <SensorZigbeeSetupButton hubs={hubs} />
            </>}
        </div>
    }


    return {
        title: 'Sensors',
        getColumns,
        parentObjects: sensors,
        getListItemProps,
        idField,
        saveObjects,
        deleteObject,
        sortObjects,
        getActions,
        getPlaceholder
    }
}